// ============================================================
// TERROR TCG — HOW TO PLAY  (MERGED · 4xS)
// One file, one door. The old hub (Tutorial.js) and the six-act
// engine (Tutorial1.js) are now a single experience:
//
//   TITLE SCREEN — cold open. Begin, or open the Grimoire.
//   ACT ENGINE   — the six-act scripted Solrend:
//     Act I    — The Shatterfield (rifts tour, opponent on field)
//     Act II   — Setup & First Draw (Anima/Khao, curio activation)
//     Act III  — Summoning (Escling → Bebi)
//     Act IV   — Combat & Transcend (Lyt vs Lyf, Beb→Champion)
//     Act V    — Soul & Supreme (Impera → U'hlti + Affinity → Supreme)
//     Act VI   — The Finale (Willie's Shade strikes; Show Stomper closes)
//
// The act engine: PHASES is a flat ordered list. Each phase optionally
// carries a `mutate(player, opp)` that runs ONCE on entry and rewrites
// state. waitFor names a flag the user must satisfy (tap a being, etc.)
// before advance is allowed.
//
// Default export: HowToPlay({ onBack, onRulebook, playerName })
//   — App.js "howtoplay" screen renders this. The old "tutorial1"
//     screen and Tutorial1.js import are retired.
// ============================================================


// ── Gilded Obsidian — pulled from the one true palette (v4) ───
// The Tutorial used to carry a private copy of the dead v3 vellum
// browns. That's gone: GM + the material layer now come straight
// from theme.js, so this screen forges from the same metal as every
// other chrome surface. SD stays for board/card/field only.
import { useState, useEffect, useCallback } from "react";
import {
  SD, GM, MANUSCRIPT_BG,
  GOLD_METAL, OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_PANEL,
  facet, facetTB, SHADOW_RAISED, SHADOW_FLOAT,
  edgeLight, bloom, textGlow, TEXT_SET,
} from "./theme";
import { UnstableWorldBG, CornerFlourish, OrnateDivider, StarDust, GlassCracks } from "./TerrorUI";
import { useStageRect } from "./RoomBackdrop"; // C5 — the one canonical room rectangle
import { CDN, cardUrl } from "./CDN"; // CDN law — one domain, one file (loose-debt sweep 2026-08-18)
import { ESCENDANTS, ESCLINGS, TOMES, IMPERA, UHLTIS, CURIOS, AFFINITIES, PROPHECIES } from "./cards";


// ── Keyframes — imperative injection (KEYFRAMES VANISH trap) ──
// Never gate a React-rendered <style> behind a module boolean; inject
// once into document.head, keyed off the DOM node actually existing.
const TUT_KEYFRAMES = `
@keyframes tutPulse {
  0%,100% { box-shadow: 0 0 0 2px #e8b84b, 0 0 16px #e8b84b66, 0 0 34px #8b5cf62e; }
  50%      { box-shadow: 0 0 0 3px #f5d878, 0 0 26px #e8b84baa, 0 0 48px #8b5cf644; }
}
@keyframes tutSweep {
  0%   { background-position: -180% 0; }
  100% { background-position: 280% 0; }
}
@keyframes tutFadeUp {
  0%   { opacity: 0; transform: translateY(14px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes tutActCard {
  0%   { opacity: 0; transform: scale(0.96); }
  14%  { opacity: 1; transform: scale(1); }
  82%  { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.02); }
}
@keyframes tutTitleGlow {
  0%,100% { text-shadow: 0 0 18px rgba(232,184,75,0.4), 0 0 40px rgba(139,92,246,0.18); }
  50%      { text-shadow: 0 0 32px rgba(245,216,120,0.7), 0 0 60px rgba(139,92,246,0.3); }
}
@keyframes tutDraw {
  0%   { stroke-dashoffset: 1; opacity: 0; }
  20%  { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 1; }
}
@keyframes tutAnts {
  0%   { opacity: 0; }
  25%  { opacity: 1; }
  100% { opacity: 1; stroke-dashoffset: -0.5; }
}
@keyframes tutRingPulse {
  0%,100% { opacity: 0.55; transform: scale(1); }
  50%      { opacity: 1;    transform: scale(1.045); }
}
@keyframes tutPointBob {
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-7px); }
}
@keyframes tutLabelIn {
  0%   { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes tutCrossIn {
  0%,55% { opacity: 0; transform: scale(0.4); }
  75%     { opacity: 1; transform: scale(1.25); }
  100%    { opacity: 1; transform: scale(1); }
}
`;
function ensureTutKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("tut-keyframes")) return;
  const el = document.createElement("style");
  el.id = "tut-keyframes";
  el.textContent = TUT_KEYFRAMES;
  document.head.appendChild(el);
}
ensureTutKeyframes(); // at module load, before any paint

const ALL_CARDS = [...ESCENDANTS, ...ESCLINGS, ...TOMES, ...IMPERA, ...UHLTIS, ...CURIOS, ...AFFINITIES, ...PROPHECIES];
const getCard = (id) => id ? ALL_CARDS.find(c => c.id === id) || null : null;

// ═══════════════════════════════════════════════════════════
// SCENARIO STATE — STARTING POSITIONS  (Rulez_v2 canon)
// ═══════════════════════════════════════════════════════════
//
// Act I tour uses a "dressed" mid-game field so the player can SEE
// every rift in context. Act II rewinds narratively and resets to a
// canon opening: 30 Anima, 3 Max Khao, Esclings on the field.
// Acts II–V build the player up under true rules. Act VI is the
// post-time-skip finale.
//
// Every number in this file is canon-checked:
//   30 Anima start · 3 Max Khao start · no-spend growth +1 (nat cap 10,
//   card ceiling 15) · Natural Transcend/Ascend free · one Ascend per
//   Turn · no Anima loss on ruin · Fractured blocks Anima · Wizard's
//   Cap +3 · Show Stomper OP3 10 Khao + 10 Anima, dim 10.
//

// ─── Act I — opening tableau (mid-game, both fields dressed) ──
const ACT1_PLAYER = (name) => ({
  name: name || "Aenarchist",
  anima: 33, khao: 6, maxKhao: 6,
  hand: [],
  necropolis: [],
  field: {
    prophecy: "proph_dragonforce",
    esclings: [
      { escling: "escling_dragon", escendant: "premier_dragon_beb",      currentLyf: 4, soul: ["p_premier_dragon_1"], natural: true },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
    ],
    tomes: [
      { id: "tome_khao_duhmdumn_champion", faceDown: false, currentLyf: 1 },
      null, null,
    ],
    curios: ["curio_wizards_cap", "curio_warlocks_robe"],
  },
});

const ACT1_OPP = {
  name: "Willie's Shade",
  anima: 30, khao: 5, maxKhao: 6,
  hand: [],
  necropolis: [],
  field: {
    prophecy: "proph_tablet_omnis",
    esclings: [
      { escling: "escling_dragon", escendant: "chaos_dragon_beb", currentLyf: 0, soul: ["p_chaos_dragon_1"], natural: true },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
    ],
    tomes: [
      { id: "tome_nothing_left", faceDown: true },
      null, null,
    ],
    curios: ["curio_jynxi_wares", null],
  },
};

// ─── Act II — canon opening: 30 Anima, 3 Max Khao ─────────────
const CLEAN_PLAYER = (name) => ({
  name: name || "Aenarchist",
  anima: 30, khao: 3, maxKhao: 3,
  hand: [],
  necropolis: [],
  field: {
    prophecy: "proph_dragonforce",
    esclings: [
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
    ],
    tomes: [null, null, null],
    curios: ["curio_wizards_cap", null],
  },
});

const CLEAN_OPP = {
  name: "Willie's Shade",
  anima: 30, khao: 3, maxKhao: 3,
  hand: [],
  necropolis: [],
  field: {
    prophecy: "proph_tablet_omnis",
    esclings: [
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
    ],
    tomes: [null, null, null],
    curios: [null, null],
  },
};

// ─── Opening hand — the mandatory 5 ──────────────────────────
const OPENING_HAND = [
  "premier_dragon_beb",
  "premier_dragon_beb",
  "premier_dragon_champion",
  "tome_khao_duhmdumn_champion",
  "tome_khao_garden",
];

// ─── Act VI — final turn (post time-skip) ─────────────────────
const ACT6_PLAYER = (name) => ({
  name: name || "Aenarchist",
  anima: 35, khao: 10, maxKhao: 10,
  hand: [],
  necropolis: ["premier_dragon_beb", "tome_khao_garden"],
  field: {
    prophecy: "proph_dragonforce",
    esclings: [
      {
        escling: "escling_dragon",
        escendant: "premier_dragon_lord",
        currentLyf: 5,
        attackedThisTurn: false,
        soul: ["p_premier_dragon_1", "p_premier_dragon_2", "p_premier_dragon_3", "u_premier_dragon_lord", "aff_holy"],
        natural: true, deviant: false, intangible: false,
      },
      {
        escling: "escling_dragon",
        escendant: "premier_dragon_champion",
        currentLyf: 4,
        attackedThisTurn: false,
        soul: ["p_premier_dragon_1", "p_premier_dragon_2"],
        natural: true, deviant: false,
      },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
    ],
    tomes: [
      { id: "tome_khao_duhmdumn_champion", faceDown: false, currentLyf: 1 },
      null, null,
    ],
    curios: ["curio_wizards_cap", "curio_warlocks_robe"],
  },
});

const ACT6_OPP = {
  name: "Willie's Shade",
  anima: 10, khao: 10, maxKhao: 10,
  hand: [],
  necropolis: ["chaos_dragon_beb", "chaos_dragon_beb"],
  field: {
    prophecy: "proph_tablet_omnis",
    esclings: [
      {
        escling: "escling_dragon",
        escendant: "chaos_dragon_lord",
        currentLyf: 0,
        attackedThisTurn: false,
        soul: ["p_chaos_dragon_1", "p_chaos_dragon_2", "p_chaos_dragon_3", "u_chaos_dragon_lord", "aff_evil"],
        natural: true, deviant: false,
      },
      {
        escling: "escling_dragon",
        escendant: "chaos_dragon_champion",
        currentLyf: 0,
        attackedThisTurn: false,
        soul: ["p_chaos_dragon_1"],
        natural: true, deviant: false,
      },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
      { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] },
    ],
    tomes: [
      { id: "tome_nothing_left", faceDown: true },
      null, null,
    ],
    curios: ["curio_jynxi_wares", null],
  },
};

// ═══════════════════════════════════════════════════════════
// PHASES — the act engine  (canon-synced to Rulez_v2)
// ═══════════════════════════════════════════════════════════
//
// Phase fields:
//   id            — unique string
//   act           — 1..6 (drives the act-progress dot row)
//   icon, title   — bubble decoration
//   body          — multi-line; \n-split into pages
//   btn           — null = no advance button (auto or waitFor)
//   highlight     — which side of the field to glow ("my_beings" etc.)
//   pulse         — which slot to pulse for tap input
//   waitFor       — a flag string; phase only advances when handler flips it
//   autoAdvanceMs — auto-advance after delay (used for opp turn / time skip)
//   mutate        — (player, opp) => { player?, opp? } applied ONCE on entry
//
const PHASES = [

  // ═══ ACT I — THE SHATTERFIELD ═══════════════════════════
  {
    id: "intro", act: 1, icon: "◆", title: "The Shatterfield",
    body: "Welcome to the Solrend, Aenarchist.\n\nThis is the Shatterfield — a fragment of the Unstable World suspended between two souls.\n\nBefore we play a single card, look around. Every rift here has a purpose.",
    btn: "Show me →",
  },
  {
    id: "rifts_esclings", act: 1, icon: "🥚", title: "Esclings — The Eternal Four",
    body: "Your four Esclings were placed before the match even began. They belong to no deck.\n\nEsclings are eternal — no Lyt Attack, no bypass, no card can touch them. They may strike opposing Escendants, but never Anima. They are not a wall: a board of nothing but Esclings does not protect your Anima.\n\nEverything you become in a Solrend is built on these four.",
    btn: "And the beings? →",
    highlight: "my_beings",
  },
  {
    id: "rifts_beings", act: 1, icon: "🐉", title: "Escendant Rifts",
    body: "When a being rises from an Escling, it stands in the same rift.\n\nYou've already started — Premier Dragon Beb, risen from a Dragon Escling.\n\nAcross the divide: Willie's Shade fields a Chaos Dragon Beb.",
    btn: "Got it →",
    highlight: "my_beings",
  },
  {
    id: "rifts_tomes", act: 1, icon: "📖", title: "Tome Rifts",
    body: "The three rifts below your beings are your Tome rifts.\n\nTomes are the cards you cast from your hand — pay the fare to activate, Khao and Anima too where the card prints a number for it, or set them face-down to bide. Most are ruined after use; those marked OPT, OP2, OP3, or ATV stay and fire again — once per full Turn, once per two, once per three. ATV is Active Till Void: it holds the rift for as long as it is active, and the moment it is voided — answered, undone, its standing condition unmet, or simply ruined where it stands — it is no longer active and it leaves.\n\nYours is Khao D'uhmdumn Champion — a shield-Tome: your adversary must ruin it before attacking another card. Willie's Shade keeps one face-down. You can't read theirs.\n\nAnd a face-down Tome is how you ANSWER. When your adversary declares something, a response window opens on their Turn. A Nihil or an Omnia may reply — Level 3 from your hand, or Level 2 or higher from a face-down rift. A Nihil voids the effect; an Omnia ruins the card. They may answer your answer, and you theirs, as long as the Khao holds out.",
    btn: "Continue →",
    highlight: "tomes",
  },
  {
    id: "rifts_curios", act: 1, icon: "💎", title: "Curio Rifts",
    body: "The two small slots flanking your Tomes are your Curio Rifts.\n\nCurios are placed before the match starts and belong to no deck. Most check something real — what you're wearing, what you carry — and auto-activate as the duel opens.\n\nYou'll see one wake up in just a moment.",
    btn: "Continue →",
    highlight: "curios",
  },
  {
    id: "rifts_prophecy", act: 1, icon: "🔮", title: "Prophecy & Necropolis",
    body: "Your Prophecy — Dragonforce — sits face-down at the edge. Optional. One per Solrend. No card can touch it.\n\nMeet its condition, pay its Khao — and you win the Solrend, whatever the Anima says. You cash it the instant the condition is true, on anyone's Turn; it interrupts the duel rather than waiting for yours. A Prophecy grants a win; it never takes one away, so both seats can win the same Solrend.\n\nBeside it waits the Necropolis, where the ruined rest. Both sides' are empty — for now.",
    btn: "Continue →",
    highlight: "prophecy",
  },
  {
    // ⚠ PLACEHOLDER PROSE (V'ael, 2026-09-08) — the FACTS are RULEZ_V2
    // ch.12; the voice is scaffolding and is caP'n's to rewrite. Before
    // this step existed a tutorial graduate had never been told the four
    // rifts have names, which is the whole of the Exile Wave's front half.
    id: "rifts_roles", act: 1, icon: "🜂", title: "The Four Rifts Have Names",
    body: "Look at your battle line again. Those four rifts are not interchangeable.\n\nLeft to right: Body, Sol, Heart, Mind. Each carries an Art — a power that belongs to the RIFT, not to the being standing in it. Move a being and it wears a different Art.\n\nBody is the Tank. Sol is the Terror. Heart is the Healer. Mind is the Warden.\n\nNone of them does anything at all for the first three full Turns. Not the activated ones, not the free ones. For three Turns a Mind is an ordinary being and can be struck down like any other.",
    btn: "And after three Turns? →",
    highlight: "my_beings",
  },
  {
    // ⚠ PLACEHOLDER PROSE — same note as the step above.
    id: "rifts_mind", act: 1, icon: "🛡", title: "The Warden, and Its Price",
    body: "Once the Arts wake, the Mind rift is shut away: while your Body rift still holds a standing, un-Fallen Escendant, nothing may Lyt-attack your Warden. Ruin the Body and leave it Fallen — or leave nothing but Esclings beside it — and the door opens. Every OTHER kind of Attack reaches a Warden freely, on any Turn.\n\nAnd a Warden is never a wall. For the one question 'may I swing at your Anima', pretend the Mind rift isn't there.\n\nThe price is silence. When your Body, Sol and Heart ALL hold Escendants, the being in your Mind rift may do nothing at all — no Lyt Attack, no stamp, no Soul power, no Exiliation. Nothing but its own Warden Art.\n\nGrowth is never silenced. You may still Ascend into it, Train onto it, bond to it. An ace in the hole.",
    btn: "Continue →",
    highlight: "my_beings",
  },
  {
    // ⚠ PLACEHOLDER PROSE — same note. RULEZ_V2 ch.22.
    id: "rifts_exile", act: 1, icon: "🕳", title: "The Exile Rift",
    body: "Beside your Necropolis sits a rift that holds nothing at the start of a duel, and is the only place in Terror a card does not come back from.\n\nThe Necropolis is where the ruined WAIT. The Exile rift is where they are forgotten: no Book, no Resurrection, no tutor, no Graveshift.\n\nEvery Aenarchist knows Exiliation — it is not a card, it is something you simply know how to do. Give up a card and take another; the fare is the level of what you gave up, 2 or 3 or 4, and it costs you no draw. Once every two full Turns.\n\nAnd there is a Ritual that buys a card back out of exile. You must be holding at least 10 Anima to reach the rift at all — below that the door is not shut, it is not there. The fare is half of everything you hold, rounded against you. Once every three full Turns, and only ever your own cards.\n\nWhat returns is Marked: ruined again it goes back to the Exile rift, it can never be Resurrected by any method, and it can never be bought back a second time. Once ever — per card, not per name.",
    btn: "Continue →",
    highlight: "exile",
  },
  {
    id: "rifts_grimoire", act: 1, icon: "📚", title: "Grimoire & Luxmoire",
    body: "Your two decks flank your hand, below.\n\nYour Grimoire — Escendants and Tomes, thirty-five cards at minimum.\n\nYour Luxmoire — Impera, U'hltis, and Affinities. Everything a Soul is made of.\n\nNow — let's rewind. I'll show you how a Solrend truly begins.",
    btn: "Take me to the opening →",
    highlight: "grimoire",
  },

  // ═══ ACT II — SETUP & THE OPENING ═══════════════════════
  {
    id: "act2_reset", act: 2, icon: "🌀", title: "The Opening",
    body: "Every Solrend opens the same way.\n\n30 Anima. 3 Maximum Khao. Four Esclings face-up on the field.\n\nAt the table, two Aenarchists throw rock-paper-scissors. Here, the Solrend Coin decides — and it has come up yours. You move first.",
    btn: "Anima first →",
    mutate: (p, o, name) => ({ player: CLEAN_PLAYER(name), opp: CLEAN_OPP }),
  },
  {
    id: "act2_anima", act: 2, icon: "♥", title: "Anima — Your Life",
    body: "Anima is your life. Dim your adversary's to zero, and the Solrend ends in your favor.\n\nIt's also fuel — many costs may be paid in Anima instead of Khao.\n\nGuard it. Spend it. Both.",
    btn: "And Khao? →",
    highlight: "my_stats",
  },
  {
    id: "act2_khao", act: 2, icon: "✦", title: "Khao — Your Fuel",
    body: "Khao refills to your Maximum at the start of your every Turn.\n\nAnd here's the secret: pass a full Turn without spending any, and your Maximum grows by one — up to a natural cap of 10. Cards can force it higher, to 15.\n\nThe +1 doesn't arrive when that Turn closes. It's waiting at the top of your next one, and the refill pours the bigger number. Never on the opening Turn, however frugal you are.\n\nPatience is power.",
    btn: "Show me the Curio →",
    highlight: "my_stats",
  },
  {
    id: "act2_curio_intro", act: 2, icon: "💎", title: "Curios Wake",
    body: "Your Wizard's Cap was placed before the match — it belongs to no deck.\n\nIt checks something real: you're wearing headwear, yes? So it wakes on its own, the instant the Solrend opens. You never press it.\n\nWatch.",
    btn: null,
    highlight: "curios",
    pulse: "curio_0",   // the eye is drawn to it; the finger is not needed
    autoAdvanceMs: 1900,
    mutate: (p, o) => ({ player: { ...p, anima: p.anima + 3 } }),
  },
  {
    id: "act2_curio_done", act: 2, icon: "✦", title: "+3 Anima",
    body: "Wizard's Cap pulses gold. Your Anima rises to 33.\n\nSmall tilts before either side moves — that's what Curios are for.",
    btn: "Deal the hand →",
    highlight: "my_stats",
  },
  {
    id: "act2_opening_hand", act: 2, icon: "🎴", title: "The Mandatory Five",
    body: "Both Aenarchists draw the mandatory opening hand — five cards. One mulligan each — this Turn only, and only before you have drawn. Once per duel.\n\nFrom then on: one draw per Turn, one source — Grimoire or Luxmoire, never both.\n\nYour hand caps at 8. Draw past it and one card goes to the Necropolis — you choose which, and you cannot end your Turn until you have.\n\nUnless the clock ends it for you. Run out of time with your hand over the cap and the newest card in it is buried for you — and you are told which one left.",
    btn: "Turn 1 →",
    mutate: (p, o) => ({ player: { ...p, hand: [...OPENING_HAND] } }),
  },
  {
    id: "act2_t1_law", act: 2, icon: "⚖", title: "First-Turn Law",
    body: "The opening Turn has four laws.\n\nNo Attacks of any kind — for either side. Not a Lyt Attack, not a Tome, not a stamp. Nothing that dims an adversary's Anima or harms an adversary's being.\n\nNo searching either Book, at any price — though a card whose own text searches resolves normally.\n\nMoving first, you may not draw from your Grimoire this Turn — only your Luxmoire. The second player may draw from either.\n\nAnd no Maximum Khao climbs on the opening Turn, however little you spend. The patient climb starts on the second — and lands as that Turn opens.",
    btn: "Then let's build →",
  },

  // ═══ ACT III — TRANSCENDING ═════════════════════════════
  {
    id: "act3_transcend_intro", act: 3, icon: "🐉", title: "Transcending",
    body: "In your hand: Premier Dragon Beb — Level 1, Lyt 4, Lyf 4.\n\nPlacing a Level 1 over an Escling is called Transcending. Same School, same Ilk — Dragon upon Dragon — makes it Natural. And Natural Transcending costs nothing.\n\nTap your Escling.",
    btn: null,
    highlight: "my_beings",
    pulse: "my_slot_0_escling",
    waitFor: "beb_transcended",
  },
  {
    id: "act3_transcended", act: 3, icon: "✨", title: "A Being Rises",
    body: "Premier Dragon Beb rises from the Escling. Zero Khao spent.\n\nYou may Transcend every Escling you hold a Level 1 for — all in the same Turn, all free.\n\nOne is enough for today.",
    btn: "End the Turn →",
    highlight: "my_beings",
  },
  {
    id: "act3_endturn", act: 3, icon: "🌙", title: "Patience Banked",
    body: "You end Turn 1 having spent no Khao.\n\nBanked — but not paid yet. A full Turn is both halves, and the Shade has yet to take its own. Stay quiet through it and the +1 is waiting at the top of your next Turn, poured straight into your pool.\n\nThe Shade moves.",
    btn: null,
    highlight: "my_stats",
    autoAdvanceMs: 2200,
  },

  // ═══ ACT IV — COMBAT & ASCENDING ════════════════════════
  {
    id: "act4_opp_transcends", act: 4, icon: "👁", title: "Willie's Shade Transcends",
    body: "Willie's Shade Transcends its own Escling — Chaos Dragon Beb. Free, like yours.\n\nLyt 8, Lyf 0. Printed at zero Lyf, it enters the field already Fractured.\n\nA glass cannon. One clean strike ruins it.",
    btn: null,
    highlight: "opp_beings",
    autoAdvanceMs: 2400,
    mutate: (p, o) => ({
      opp: {
        ...o,
        // R91 — this is the Shade's OPENING Turn (half-turn 1) and
        // FIRST_KHAO_TURN is 2. No climb for it either; it is still on 3.
        khao: 3,
        maxKhao: 3,
        field: {
          ...o.field,
          esclings: [
            { escling: "escling_dragon", escendant: "chaos_dragon_beb", currentLyf: 0, soul: [], natural: true },
            o.field.esclings[1], o.field.esclings[2], o.field.esclings[3],
          ],
        },
      },
    }),
  },
  {
    id: "act4_combat_intro", act: 4, icon: "⚔", title: "Combat — Lyt vs Lyf",
    body: "Your Turn — Turn 2. Your banked +1 arrives as it opens: Maximum 4, and your pool fills to 4. Lyt Attacks are legal now. Each being strikes once per Turn unless a card says otherwise — and a Turn is BOTH halves, yours and theirs.\n\nYou do not have to declare anything to swing. But there is a red ATTACK PHASE button, and when you declare your Attack phase with it your hand shuts — it is one-way, and nothing new may be played for the rest of your half-turn. Only Lyt Attacks, what is already fixed to a being's Soul, your stamps, and the Arts.\n\nSo play everything you mean to play first. We are not pressing it today — watch what that buys us.\n\nCombat is Lyt against Lyf. Less than their Lyf — you wound it. Equal or greater — Fractured, Lyf to zero. Strike a Fractured being — ruined.\n\nAnd move. You get 45 seconds a half-turn, and a Turn nobody ends is ended for you. The clock holds whenever a response window is open — while you answer, and while your adversary answers you — and through a whole chain. For nothing else.\n\nChaos Beb is already Fractured. Tap your Beb.",
    btn: null,
    highlight: "my_beings",
    pulse: "my_slot_0",
    waitFor: "attack_done",
    mutate: (p, o) => ({ player: { ...p, khao: 4, maxKhao: 4 } }),
  },
  {
    id: "act4_after_attack", act: 4, icon: "💀", title: "Ruined",
    body: "Chaos Dragon Beb is ruined. A Level 1 with nothing beneath it goes to the Necropolis — only the Escling remains.\n\nAnd hear this: Esclings don't block. With no Escendant standing, your beings may strike the Shade's Anima directly — next Turn.\n\nThe Necropolis is not the end. For 2 Khao or Anima you can raise a Beb back onto an empty Escling — 4 for a Champion or a Lord — and they must return in Ascension order, never straight onto a bare Escling. One paid rite per Turn. And each individual card comes back 3 times if it was a Beb, twice a Champion, once a Lord: every method that returns it spends from that count, and two copies of one name are two separate beings with two separate counts.\n\nIf your Grimoire ever empties: pay 10 Anima and Graveshift — your hand and your whole Necropolis fold into a new Book. You cannot lose a Solrend by running out of cards. Your Exile rift takes no part in it.\n\nBut your Beb wants to grow first.",
    btn: "What else can a being do? →",
    highlight: "opp_beings",
  },
  {
    // ⚠ PLACEHOLDER PROSE (V'ael, 2026-09-14) — the FACTS are RULEZ_V2
    // ch.11 and rules.PRIMERAS/PRIMERA_COST; the voice is scaffolding and
    // is caP'n's to rewrite. Before this step existed, a tutorial
    // graduate had never heard the word Primera.
    id: "act4_stamps", act: 4, icon: "◈", title: "Primera — The Innate Stamps",
    body: "Every being carries two powers that are not cards. One from its Ilk, one from its School — the two words printed on its own face, top-left and top-right. These are its Primera stamps.\n\nThere is nothing to draw and nothing to attach. Press one and pay the being's Level — 1, 2 or 3, in Khao or in Anima, never a mix.\n\nOne stamp per being per Turn, and one use per word per Turn across your whole field. Dragon is one word on both axes: spend it as an Ilk and no being of yours may spend it as a School that Turn.\n\nRemember this one. A Phantom stamp dims your adversary's Anima directly, straight through a wall of standing beings. Some Solrends have no other door.",
    btn: "Then grow →",
    highlight: "my_beings",
  },
  {
    id: "act4_ascend", act: 4, icon: "👑", title: "Ascending",
    body: "Playing the next level of the same School and Ilk over a being is Ascending. Natural — and free.\n\nOne Ascend per Turn — Natural or Deviant, they share the one budget. A handful of cards and stamps print their own way past it; nothing else does.\n\nYou hold Premier Dragon Champion. I'll play it now.",
    btn: null,
    autoAdvanceMs: 2400,
    mutate: (p, o) => ({
      player: {
        ...p,
        hand: p.hand.filter(id => id !== "premier_dragon_champion"),
        field: {
          ...p.field,
          esclings: [
            { ...p.field.esclings[0], escendant: "premier_dragon_champion", currentLyf: 4 },
            p.field.esclings[1], p.field.esclings[2], p.field.esclings[3],
          ],
        },
      },
    }),
  },
  {
    id: "act4_ascended", act: 4, icon: "✨", title: "Champion",
    body: "Premier Dragon Champion — Lyt 5, Lyf 4. On Ascension, Lyf resets to the new card's printed value.\n\nYou spent nothing again — banked again. That +1 lands as your next Turn opens, not now.\n\nThe Shade hesitates. Nothing in its hand breaks a Champion. It sets a face-down Tome — free, like yours — and passes. It spent nothing on its own quiet Turn either, so its Maximum climbs to 4 as it sits down. It moved second; it is one half-turn behind you all game.",
    btn: "The Soul awaits →",
    highlight: "my_beings",
    autoAdvanceMs: null,
    mutate: (p, o) => ({
      // R132 — THE PLAYER DOES NOT CLIMB IN THIS STEP. Turn 2 was quiet,
      // so the +1 lands as Turn 3 OPENS; writing it here paid it half a
      // Turn early, which is the drift caP'n's ruling closed. Act V's
      // time-skip carries the Maximum on from 4.
      opp: {
        ...o,
        // R91 + the patient climb: this is the Shade's FIRST legal climb —
        // the opening Turn it spent nothing on, paid out as its second
        // Turn opens. Setting a Tome face-down costs nothing (canon), so
        // it stays on the climb.
        khao: 4, maxKhao: 4,
        field: { ...o.field, tomes: [{ id: "tome_nothing_left", faceDown: true }, null, null] },
      },
    }),
  },

  // ═══ ACT V — SOUL & SUPREME ═══════════════════════════
  {
    id: "act5_soul_intro", act: 5, icon: "✦", title: "The Soul",
    body: "Everything a being carries — Impera, U'hlti, Affinity — is its Soul.\n\nSouls survive. Ruin one level and the being descends; the Soul rides the survivor down.\n\nWe skip a few quiet Turns. Patience has grown your Maximum to 8.",
    btn: "Show me Impera →",
    highlight: "my_beings",
    mutate: (p, o) => ({ player: { ...p, khao: 8, maxKhao: 8 } }),
  },
  {
    id: "act5_train1", act: 5, icon: "⭐", title: "Impera — Training",
    body: "Impera live in your Luxmoire. Two ways to reach them, each consuming your Turn's single draw:\n\nDraw the Luxmoire blind — or pay 5 Khao or 5 Anima to search it. But a search finds an Impera and nothing else: your U'hltis and your Affinity live there too, and they are drawn, never tutored.\n\nToday you draw blind — and fate is kind. Good Morning, your Level 1 Impera.",
    btn: "Train it →",
    mutate: (p, o) => ({ player: { ...p, hand: [...p.hand, "p_premier_dragon_1"] } }),
  },
  {
    id: "act5_train1_done", act: 5, icon: "✨", title: "Natural Training",
    body: "School and Ilk match — the Training is Natural.\n\nPay the card's cost — 6 Khao — and Good Morning joins the Soul.\n\nOne Impera per level. Three levels. Three Impera is a full set.",
    btn: null,
    highlight: "my_beings",
    autoAdvanceMs: 2200,
    mutate: (p, o) => ({
      player: {
        ...p,
        khao: p.khao - 6,
        hand: p.hand.filter(id => id !== "p_premier_dragon_1"),
        field: {
          ...p.field,
          esclings: [
            { ...p.field.esclings[0], soul: [...p.field.esclings[0].soul, "p_premier_dragon_1"] },
            p.field.esclings[1], p.field.esclings[2], p.field.esclings[3],
          ],
        },
      },
    }),
  },
  {
    id: "act5_train2", act: 5, icon: "🔎", title: "The Paid Search",
    body: "Next Turn. This time, the search: 5 Anima buys Social Dragonfly straight from your Luxmoire.\n\nAnima as fuel — just as promised.\n\nThen 7 Khao trains it in. Two Impera in the Soul.",
    btn: null,
    highlight: "my_beings",
    autoAdvanceMs: 2400,
    mutate: (p, o) => ({
      player: {
        ...p,
        anima: p.anima - 5,
        khao: 8 - 7,
        maxKhao: 8,
        field: {
          ...p.field,
          esclings: [
            { ...p.field.esclings[0], soul: [...p.field.esclings[0].soul, "p_premier_dragon_2"] },
            p.field.esclings[1], p.field.esclings[2], p.field.esclings[3],
          ],
        },
      },
    }),
  },
  {
    id: "act5_lord", act: 5, icon: "👑", title: "Ascend — Lord",
    body: "Turns pass. Your Grimoire yields Premier Dragon Lord, and you spend your one Ascend of the Turn.\n\nLyt 5, Lyf 5 — the printed values take hold.\n\nAnd your Maximum Khao touches its natural cap: 10.",
    btn: null,
    autoAdvanceMs: 2400,
    mutate: (p, o) => ({
      player: {
        ...p,
        khao: 10, maxKhao: 10,
        field: {
          ...p.field,
          esclings: [
            { ...p.field.esclings[0], escendant: "premier_dragon_lord", currentLyf: 5 },
            p.field.esclings[1], p.field.esclings[2], p.field.esclings[3],
          ],
        },
      },
    }),
  },
  {
    id: "act5_lvl3_uhlti", act: 5, icon: "✦", title: "Level 3 + U'hlti",
    body: "Destined for Greatness — the Level 3 Impera, 10 Khao. The set is full.\n\nThen your U'hltima — Show Stomper. Every U'hlti names its owner, and only a Natural being may bear one.\n\nYour Lord is Natural to the bone. Four cards in the Soul.",
    btn: null,
    autoAdvanceMs: 2600,
    mutate: (p, o) => ({
      player: {
        ...p,
        khao: 0,
        field: {
          ...p.field,
          esclings: [
            { ...p.field.esclings[0], soul: [...p.field.esclings[0].soul, "p_premier_dragon_3", "u_premier_dragon_lord"] },
            p.field.esclings[1], p.field.esclings[2], p.field.esclings[3],
          ],
        },
      },
    }),
  },
  {
    // ⚠ THE SEALED NAME IS NOT IN THIS STEP AND MUST NOT COME BACK.
    // caP'n's never-say list is X'aol and Y'Tyranuz, and the tutorial's
    // narrator is J'ynxi — so a name in this prose is a name in a
    // character's mouth, which is the game telling you. The CARD FACE
    // beside this bubble carries the name and always has: an Affinity's
    // printed label is a mechanic, not a mention, and that is the whole
    // of the exemption. Same law as Onboarding.js's ACT III banner.
    id: "act5_affinity", act: 5, icon: "👑", title: "The Affinity — The Final Bond",
    body: "The final bond: an Affinity. One per Luxmoire — and only a Natural Level 3 already bearing its U'hlti may take one.\n\nThe Holy Affinity. You will learn whose name is on it soon enough — not from me.\n\nBind it.",
    btn: null,
    autoAdvanceMs: 2400,
    mutate: (p, o) => ({
      player: {
        ...p,
        field: {
          ...p.field,
          esclings: [
            { ...p.field.esclings[0], soul: [...p.field.esclings[0].soul, "aff_holy"] },
            p.field.esclings[1], p.field.esclings[2], p.field.esclings[3],
          ],
        },
      },
    }),
  },
  {
    id: "act5_supreme", act: 5, icon: "✨", title: "A'AETERNIS",
    body: "Natural Level 3. Three Impera — one per level. U'hlti. Affinity.\n\nThat exact anatomy is a Supreme Being.\n\nAnd the Holy Supreme bears a name: A'aeternis. You've forged in a handful of Turns what most Aenarchists chase for a lifetime.",
    btn: "What now? →",
    highlight: "my_beings",
  },

  // ═══ ACT VI — THE FINALE (post time-skip) ══════════════
  {
    id: "act6_timeskip", act: 6, icon: "⏳", title: "Many Turns Later...",
    body: "We skip ahead.\n\nTurns of trades, ruined beings, mended Lyf. The Shatterfield spares neither side.\n\nThis is the position you fight to. Look at it.",
    btn: null,
    autoAdvanceMs: 2800,
    mutate: (p, o, name) => ({ player: ACT6_PLAYER(name), opp: ACT6_OPP }),
  },
  {
    id: "act6_position", act: 6, icon: "⚔", title: "Final Turn",
    body: "Willie's Shade — 10 Anima. One good strike from the dark.\n\nIts Chaos Dragon Lord wears the same anatomy yours does — three Impera, an U'hltima, an Affinity. A Supreme Being, and the Evil bond crowns it an A'oeternis.\n\nBut it stands at Lyt 10, printed Lyf 0 — born Fractured. And Fractured beings still stand, still BLOCK your path to Anima.\n\nYour shield-Tome has held all game: Willie's Shade must ruin Khao D'uhmdumn before attacking anything else. Their Turn.",
    btn: "Brace yourself →",
  },
  {
    id: "opp_turn", act: 6, icon: "👁", title: "Willie's Shade Moves!",
    body: "Willie's Shade flips its face-down Tome — Nothing Left.\n\n3 Khao, plus 1 of its own Anima. The Shade dims itself to 9...\n\n...and your shield-Tome is ruined. The path to your A'aeternis is clear.",
    btn: null,
    highlight: "opp_beings",
    autoAdvanceMs: 2600,
  },
  {
    id: "opp_attacks", act: 6, icon: "💀", title: "Your A'aeternis Fractured!",
    body: "Chaos Dragon Lord swings. Lyt 10 against your Lyf 5 — Fractured. Your Supreme's Lyf drops to zero.\n\nIt still stands — Fractured beings stand and block. Survive your own Turn, and its Lyf restores in full at the Turn's end.\n\nBut why wait to mend? It's YOUR turn. End this.",
    btn: "End this →",
    highlight: "my_beings",
  },
  {
    id: "activate_uhlti", act: 6, icon: "✦", title: "Activate Show Stomper",
    body: "Tap your A'aeternis on the field.\n\nOpen its Soul — Show Stomper waits inside.\n\nOP3: dim your adversary's Anima by 10. The fare: 10 Khao and 10 Anima.",
    btn: null,
    highlight: "my_slot_0",
    pulse: "my_slot_0",
    waitFor: "soul_opened",
  },
  {
    id: "soul_open", act: 6, icon: "◈", title: "The Soul of a Supreme",
    // The Affinity is named by its own card in the open Soul beside this
    // list; the list itself does not speak it. See act5_affinity.
    body: "Your A'aeternis's Soul:\n\n◆ Three Impera, one per level\n◈ Show Stomper — the U'hltima\n◆ The Holy Affinity\n\nYou hold exactly 10 Khao and 35 Anima. Pay everything. Activate Show Stomper.",
    btn: null,
    highlight: "soul_modal",
    pulse: "show_stomper",
    waitFor: "uhlti_activated",
  },
  {
    id: "victory", act: 6, icon: "🏆", title: "The Solrend is Yours, A'aeternis.",
    body: "Ten Anima, dimmed. Willie's Shade held nine.\n\nZero. The Solrend ends in your favor.\n\nSetup. Transcending. Combat. Ascension. The Soul. The Supreme. You've walked the bones of Terror.\n\nWelcome, A'aeternis.",
    btn: "Return to Plaza →",
    highlight: null,
  },
];

// ═══════════════════════════════════════════════════════════
// GUIDE LAYER — arrows, rings & pointers that anchor new terms
// to the exact rift/card/stat they name.
// ═══════════════════════════════════════════════════════════
//
// Every annotatable element on the field carries a data-tut anchor:
//   my-anima, my-khao, opp-anima, opp-khao
//   my-slot-0/1/2, opp-slot-0, my-tome-0, opp-tome-0,
//   my-curio-0, my-curio-1, my-prophecy, my-necropolis,
//   opp-necropolis, my-lux, my-grim, my-hand
//
// GUIDES maps phase id → visual marks. Kept to 1–3 per phase — the
// goal is a finger pointing at the term, not a diagram.
//   { type:"ring",  target, color, label, cross?, big? }
//   { type:"arrow", from, to, color, label, cross?, dashed? }
//   { type:"point", target, color, label }
//
// Guide-mark colors — the v4 chrome family. "teal" keeps its key name
// (many GUIDES entries reference GC.teal) but is now moonlit silver so
// annotation marks read as the same metal as the rest of the chrome.
const GC = { gold: "#e8b84b", red: "#e0607a", teal: "#b8c0d4" };

const GUIDES = {
  // ── Act I — the tour ──
  rifts_esclings: [
    { type: "ring",  target: "my-slot-1", color: GC.gold, label: "Escling" },
    { type: "arrow", from: "my-slot-1", to: "opp-slot-0", color: GC.teal, label: "may strike beings" },
    { type: "arrow", from: "my-slot-1", to: "opp-anima", color: GC.red, cross: true, dashed: true, label: "never Anima" },
  ],
  rifts_beings: [
    { type: "ring", target: "my-slot-0",  color: GC.gold, label: "your Escendant" },
    { type: "ring", target: "opp-slot-0", color: GC.red,  label: "the adversary's" },
  ],
  rifts_tomes: [
    { type: "ring", target: "my-tome-0",  color: GC.teal, label: "your Tome" },
    { type: "ring", target: "opp-tome-0", color: GC.red,  label: "face-down — unreadable" },
  ],
  rifts_curios: [
    { type: "ring", target: "my-curio-0", color: GC.gold, label: "Curio" },
    { type: "ring", target: "my-curio-1", color: GC.gold, label: "Curio" },
  ],
  rifts_prophecy: [
    { type: "ring", target: "my-prophecy",   color: GC.gold, label: "Prophecy" },
    { type: "ring", target: "my-necropolis", color: GC.teal, label: "Necropolis" },
  ],
  rifts_exile: [
    { type: "ring",  target: "my-exile",       color: GC.teal, label: "Exile — nothing returns" },
    { type: "arrow", from: "my-necropolis", to: "my-exile", color: GC.red, dashed: true, label: "the ruined WAIT · the exiled do not" },
  ],
  rifts_grimoire: [
    { type: "ring", target: "my-grim", color: GC.gold, label: "Grimoire" },
    { type: "ring", target: "my-lux",  color: GC.teal, label: "Luxmoire" },
  ],

  // ── Act II — setup & the opening ──
  act2_anima: [
    { type: "point", target: "my-anima", color: GC.red, label: "your life" },
  ],
  act2_khao: [
    { type: "point", target: "my-khao", color: GC.gold, label: "your fuel" },
  ],
  act2_curio_intro: [
    { type: "point", target: "my-curio-0", color: GC.gold, label: "wakes on its own" },
  ],
  act2_opening_hand: [
    { type: "ring", target: "my-hand", color: GC.gold, label: "the mandatory five" },
  ],
  act2_t1_law: [
    { type: "ring",  target: "my-grim", color: GC.red, cross: true, label: "sealed — Turn 1" },
    { type: "arrow", from: "my-slot-1", to: "opp-slot-0", color: GC.red, cross: true, dashed: true, label: "no Lyt Attacks" },
  ],

  // ── Act III — transcending ──
  act3_transcend_intro: [
    { type: "arrow", from: "my-hand", to: "my-slot-0", color: GC.gold, label: "play your Beb" },
  ],
  act3_transcended: [
    { type: "ring", target: "my-slot-0", color: GC.gold, label: "Level 1 · 0 Khao" },
  ],
  act3_endturn: [
    { type: "point", target: "my-khao", color: GC.gold, label: "+1 banked" },
  ],

  // ── Act IV — combat & ascending ──
  act4_opp_transcends: [
    { type: "ring", target: "opp-slot-0", color: GC.red, label: "Lyf 0 — born Fractured" },
  ],
  act4_combat_intro: [
    { type: "arrow", from: "my-slot-0", to: "opp-slot-0", color: GC.red, label: "Lyt 4 vs Lyf 0" },
    // R132 — the quiet opening Turn is paid HERE, as Turn 2 opens.
    { type: "point", target: "my-khao", color: GC.gold, label: "+1 Maximum" },
  ],
  act4_after_attack: [
    { type: "ring",  target: "opp-necropolis", color: GC.teal, label: "ruined — to the Necropolis" },
    { type: "arrow", from: "my-slot-0", to: "opp-anima", color: GC.gold, dashed: true, label: "path opens next Turn" },
  ],
  act4_ascend: [
    { type: "arrow", from: "my-hand", to: "my-slot-0", color: GC.gold, label: "Ascend — free" },
  ],
  act4_ascended: [
    { type: "point", target: "opp-khao", color: GC.red, label: "+1 Maximum" },
  ],

  // ── Act V — soul & supreme ──
  act5_soul_intro: [
    { type: "ring", target: "my-slot-0", color: GC.gold, label: "the Soul lives here" },
  ],
  act5_train1: [
    { type: "arrow", from: "my-lux", to: "my-hand", color: GC.teal, label: "Impera live in the Luxmoire" },
  ],
  act5_train1_done: [
    { type: "arrow", from: "my-hand", to: "my-slot-0", color: GC.gold, label: "6 Khao — trained" },
  ],
  act5_train2: [
    { type: "point", target: "my-anima", color: GC.red, label: "−5 · the paid search" },
  ],
  act5_lord: [
    { type: "arrow", from: "my-grim", to: "my-slot-0", color: GC.gold, label: "the Lord rises" },
  ],
  act5_lvl3_uhlti: [
    { type: "ring", target: "my-slot-0", color: GC.gold, label: "✦4 in the Soul" },
  ],
  act5_affinity: [
    { type: "ring", target: "my-slot-0", color: GC.gold, label: "the final bond" },
  ],
  act5_supreme: [
    { type: "ring", target: "my-slot-0", color: GC.gold, big: true, label: "A'AETERNIS" },
  ],

  // ── Act VI — the finale ──
  act6_position: [
    { type: "ring", target: "opp-anima", color: GC.red,  label: "10 — one strike away" },
    { type: "ring", target: "my-tome-0", color: GC.teal, label: "your shield" },
  ],
  opp_turn: [
    { type: "arrow", from: "opp-tome-0", to: "my-tome-0", color: GC.red, label: "Nothing Left" },
  ],
  opp_attacks: [
    { type: "arrow", from: "opp-slot-0", to: "my-slot-0", color: GC.red, label: "Lyt 10" },
  ],
  activate_uhlti: [
    { type: "point", target: "my-slot-0", color: GC.gold, label: "open the Soul" },
  ],
};

// ── Geometry helpers ─────────────────────────────────────────
function tutGetRect(anchor) {
  if (typeof document === "undefined") return null;
  const el = document.querySelector(`[data-tut="${anchor}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return null;
  return { x: r.left, y: r.top, w: r.width, h: r.height, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
}

// Point on a rect's border (plus padding) toward a target point.
function tutEdgePoint(rect, tx, ty, pad = 8) {
  const dx = tx - rect.cx, dy = ty - rect.cy;
  if (dx === 0 && dy === 0) return { x: rect.cx, y: rect.cy };
  const hw = rect.w / 2 + pad, hh = rect.h / 2 + pad;
  const sx = dx !== 0 ? hw / Math.abs(dx) : Infinity;
  const sy = dy !== 0 ? hh / Math.abs(dy) : Infinity;
  const s = Math.min(sx, sy);
  return { x: rect.cx + dx * s, y: rect.cy + dy * s };
}

function GuideLayer({ phase, player, opp }) {
  const guides = GUIDES[phase] || [];
  const [marks, setMarks] = useState([]);

  useEffect(() => {
    if (!guides.length) { setMarks([]); return; }
    let raf1, raf2;
    const measure = () => {
      const out = [];
      for (const g of guides) {
        if (g.type === "arrow") {
          const a = tutGetRect(g.from), b = tutGetRect(g.to);
          if (a && b) out.push({ ...g, a, b });
        } else {
          const r = tutGetRect(g.target);
          if (r) out.push({ ...g, r });
        }
      }
      setMarks(out);
    };
    // Double-rAF: measure after the phase's mutations have painted.
    raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(measure); });
    window.addEventListener("resize", measure);
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); window.removeEventListener("resize", measure); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, player, opp]);

  if (!marks.length) return null;

  const labelPill = (color) => ({
    position: "fixed", zIndex: 41, pointerEvents: "none",
    transform: "translate(-50%, 0)",
    background: OBSIDIAN_SLAB,
    border: `1px solid ${color}99`,
    clipPath: facetTB(5),
    padding: "3px 10px", fontSize: 9, fontWeight: 900, letterSpacing: 1,
    color, fontFamily: GM.fontUI, whiteSpace: "nowrap",
    boxShadow: `${edgeLight(color, 0.3)}, 0 2px 12px rgba(0,0,0,0.75)`,
    filter: `drop-shadow(0 0 8px ${color}33)`,
    textShadow: TEXT_SET,
    animation: "tutLabelIn 0.5s ease 0.35s both",
  });

  return (
    <div key={phase} style={{ position: "fixed", inset: 0, zIndex: 40, pointerEvents: "none" }}>
      <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}>
        <defs>
          {Object.entries(GC).map(([k, c]) => (
            <marker key={k} id={`tutArrow-${k}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
            </marker>
          ))}
        </defs>

        {marks.map((g, i) => {
          // ── RING ─────────────────────────────────────────
          if (g.type === "ring") {
            const pad = g.big ? 10 : 6;
            return (
              <g key={i}>
                <rect
                  x={g.r.x - pad} y={g.r.y - pad} width={g.r.w + pad * 2} height={g.r.h + pad * 2}
                  rx={12} fill="none" stroke={g.color} strokeWidth={g.big ? 2.5 : 2}
                  style={{
                    filter: `drop-shadow(0 0 ${g.big ? 10 : 6}px ${g.color})`,
                    animation: "tutRingPulse 1.6s ease-in-out infinite",
                    transformBox: "fill-box", transformOrigin: "center",
                  }}
                />
                {g.cross && (
                  <g style={{ animation: "tutCrossIn 0.9s ease both", transformBox: "fill-box", transformOrigin: "center" }}>
                    <line x1={g.r.x - pad} y1={g.r.y - pad} x2={g.r.x + g.r.w + pad} y2={g.r.y + g.r.h + pad} stroke={g.color} strokeWidth={2.5} strokeLinecap="round" />
                    <line x1={g.r.x + g.r.w + pad} y1={g.r.y - pad} x2={g.r.x - pad} y2={g.r.y + g.r.h + pad} stroke={g.color} strokeWidth={2.5} strokeLinecap="round" />
                  </g>
                )}
              </g>
            );
          }

          // ── ARROW ────────────────────────────────────────
          if (g.type === "arrow") {
            const start = tutEdgePoint(g.a, g.b.cx, g.b.cy);
            const end   = tutEdgePoint(g.b, g.a.cx, g.a.cy, 12);
            const mx = (start.x + end.x) / 2, my = (start.y + end.y) / 2;
            const dx = end.x - start.x, dy = end.y - start.y;
            const dist = Math.hypot(dx, dy) || 1;
            // Perpendicular bow — arcs the line so it never draws flat over cards
            const bow = Math.min(60, dist * 0.22);
            const cx = mx - (dy / dist) * bow;
            const cy = my + (dx / dist) * bow;
            const colorKey = Object.keys(GC).find(k => GC[k] === g.color) || "gold";
            return (
              <g key={i}>
                <path
                  d={`M ${start.x} ${start.y} Q ${cx} ${cy} ${end.x} ${end.y}`}
                  fill="none" stroke={g.color} strokeWidth={2.5} strokeLinecap="round"
                  pathLength="1"
                  markerEnd={`url(#tutArrow-${colorKey})`}
                  style={{
                    strokeDasharray: g.dashed ? "0.05 0.035" : "1",
                    filter: `drop-shadow(0 0 5px ${g.color}aa)`,
                    animation: g.dashed
                      ? "tutAnts 2.4s linear infinite"
                      : "tutDraw 0.8s ease-out both",
                  }}
                />
                {g.cross && (
                  <g style={{ animation: "tutCrossIn 1.1s ease both", transformBox: "fill-box", transformOrigin: "center" }}>
                    <line x1={cx - 9} y1={cy - 9} x2={cx + 9} y2={cy + 9} stroke={g.color} strokeWidth={3.5} strokeLinecap="round" />
                    <line x1={cx + 9} y1={cy - 9} x2={cx - 9} y2={cy + 9} stroke={g.color} strokeWidth={3.5} strokeLinecap="round" />
                  </g>
                )}
              </g>
            );
          }

          return null;
        })}
      </svg>

      {/* Labels & pointers — HTML for crisp text */}
      {marks.map((g, i) => {
        if (g.type === "ring" && g.label) {
          const below = g.r.cy < window.innerHeight * 0.75;
          return (
            <div key={`l${i}`} style={{ ...labelPill(g.color), left: g.r.cx, top: below ? g.r.y + g.r.h + 12 : undefined, bottom: below ? undefined : window.innerHeight - g.r.y + 12 }}>
              {g.label}
            </div>
          );
        }
        if (g.type === "arrow" && g.label) {
          const mx = (g.a.cx + g.b.cx) / 2, my = (g.a.cy + g.b.cy) / 2;
          const dx = g.b.cx - g.a.cx, dy = g.b.cy - g.a.cy;
          const dist = Math.hypot(dx, dy) || 1;
          const bow = Math.min(60, dist * 0.22);
          return (
            <div key={`l${i}`} style={{ ...labelPill(g.color), left: mx - (dy / dist) * (bow + 16), top: my + (dx / dist) * (bow + 16) - 10 }}>
              {g.label}
            </div>
          );
        }
        if (g.type === "point") {
          const nearTop = g.r.y < 90;
          return (
            <div key={`l${i}`} style={{ position: "fixed", zIndex: 41, pointerEvents: "none", left: g.r.cx, top: nearTop ? g.r.y + g.r.h + 6 : g.r.y - 34, transform: "translateX(-50%)", display: "flex", flexDirection: nearTop ? "column-reverse" : "column", alignItems: "center", gap: 2 }}>
              <div style={{ background: `${GM.page}f0`, border: `1px solid ${g.color}88`, borderRadius: 3, padding: "3px 9px", fontSize: 9, fontWeight: 900, letterSpacing: 1, color: g.color, fontFamily: GM.font, whiteSpace: "nowrap", boxShadow: `0 2px 12px rgba(0,0,0,0.7)`, animation: "tutLabelIn 0.5s ease both" }}>{g.label}</div>
              <div style={{ fontSize: 13, color: g.color, textShadow: `0 0 8px ${g.color}`, animation: "tutPointBob 1.1s ease-in-out infinite", lineHeight: 1 }}>{nearTop ? "▲" : "▼"}</div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SIZES (matching real Shatterfield)
// ═══════════════════════════════════════════════════════════
const SZ = {
  normal: { width: 72, height: 100 },
  small:  { width: 52, height: 72 },
  tiny:   { width: 40, height: 56 },
  hand:   { width: 42, height: 56 },
};

// ═══════════════════════════════════════════════════════════
// FIELD CARD
// ═══════════════════════════════════════════════════════════
function FC({ cardId, size = "normal", faceDown = false, currentLyf, flipped, onClick, pulse, highlight, fallen, soulCount = 0 }) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [cardId]);
  const card = getCard(cardId);
  const dim = SZ[size] || SZ.normal;
  const isSupreme = soulCount >= 5;
  const lyf = currentLyf !== null && currentLyf !== undefined ? currentLyf : (card?.lyf ?? 0);

  const borderColor = pulse ? SD.gold
    : isSupreme ? `${SD.gold}`
    : highlight ? SD.purpleLight
    : "#5ec4f055";

  if (faceDown) return (
    <div style={{ ...dim, borderRadius: 7, background: `linear-gradient(145deg, ${SD.void3}, ${SD.void2})`, border: `1px solid ${SD.void4}`, display: "flex", alignItems: "center", justifyContent: "center", transform: flipped ? "rotate(180deg)" : "none", flexShrink: 0, opacity: 0.85 }}>
      <div style={{ fontSize: size === "tiny" ? 10 : 16, color: SD.void4 }}>◆</div>
    </div>
  );

  if (!card) return (
    <div style={{ ...dim, borderRadius: 7, background: `${SD.void3}88`, border: `1px solid ${SD.void4}`, display: "flex", alignItems: "center", justifyContent: "center", transform: flipped ? "rotate(180deg)" : "none", flexShrink: 0 }}>
      <span style={{ fontSize: 18, opacity: 0.3 }}>🌀</span>
    </div>
  );

  return (
    <div onClick={onClick} style={{
      position: "relative", ...dim, borderRadius: 7, overflow: "hidden", flexShrink: 0,
      border: `1px solid ${borderColor}`,
      cursor: onClick ? "pointer" : "default",
      transform: flipped ? "rotate(180deg)" : "none",
      opacity: fallen ? 0.5 : 1,
      boxShadow: pulse ? `0 0 0 3px ${SD.gold}, 0 0 24px ${SD.gold}88`
        : isSupreme && !flipped ? `0 0 14px ${SD.gold}55`
        : highlight ? `0 0 10px ${SD.purpleLight}44`
        : "none",
      animation: pulse ? "tutPulse 1s ease-in-out infinite" : "none",
      transition: "box-shadow 0.3s",
    }}>
      {!imgFailed && (
        <img src={cardUrl(cardId)} alt={card.name}
          onError={() => setImgFailed(true)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      )}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 38%, rgba(0,0,0,0.75) 100%)" }} />

      {size !== "tiny" && card && (
        <>
          <div style={{ position: "absolute", top: 3, left: 4, right: 4, fontSize: (size === "small" || size === "hand") ? 6 : 7, color: "#fff", fontFamily: GM.font, textShadow: "0 1px 3px rgba(0,0,0,0.9)", lineHeight: 1.2 }}>{card.name}</div>
          {size === "normal" && (
            <>
              {/* Esclings are eternal — no Lyf badge (a red 0 would read as Fractured) */}
              {card.type !== "Escling" && (
                <div style={{ position: "absolute", bottom: 3, left: 4, fontSize: 8, color: lyf === 0 ? SD.red : "#5ec4f0", fontWeight: 900 }}>🛡{lyf}</div>
              )}
              <div style={{ position: "absolute", bottom: 3, right: 4, fontSize: 8, color: "#f87171", fontWeight: 900 }}>⚔{card.lyt}</div>
              {soulCount > 0 && <div style={{ position: "absolute", top: 3, right: 3, fontSize: 7, color: SD.gold, fontWeight: 900, background: `${SD.void3}cc`, borderRadius: 3, padding: "1px 3px" }}>✦{soulCount}</div>}
              {isSupreme && <div style={{ position: "absolute", bottom: 14, left: 0, right: 0, textAlign: "center", fontSize: 7, color: SD.gold, fontWeight: 900, textShadow: `0 0 6px ${SD.gold}` }}>SUPREME</div>}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Empty rift ────────────────────────────────────────────────
function EmptyRift({ size = "normal", flipped, highlight, pulse, onClick }) {
  const dim = SZ[size] || SZ.normal;
  return (
    <div onClick={onClick} style={{
      ...dim, borderRadius: 7,
      background: `${SD.void3}44`,
      border: `1px dashed ${pulse ? SD.gold : highlight ? SD.purpleLight + "66" : SD.void4}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      transform: flipped ? "rotate(180deg)" : "none",
      flexShrink: 0,
      cursor: onClick ? "pointer" : "default",
      boxShadow: pulse ? `0 0 0 3px ${SD.gold}, 0 0 22px ${SD.gold}88` : "none",
      animation: pulse ? "tutPulse 1s ease-in-out infinite" : "none",
    }}>
      <div style={{ width: "60%", height: "60%", borderRadius: 4, border: `1px dashed ${SD.void4}` }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PLAYER SIDE — matches real Shatterfield layout
// ═══════════════════════════════════════════════════════════
function PlayerSide({ player, isMe, phase, onSlot0Escling, onSlot0Being }) {
  const field = player.field || {};
  const esclings = field.esclings || [];
  const tomes = [field.tomes?.[0] ?? null, field.tomes?.[1] ?? null, field.tomes?.[2] ?? null];
  const curios = [field.curios?.[0] ?? null, field.curios?.[1] ?? null];
  const prophecy = field.prophecy || null;

  // Highlights and pulses driven by current phase id
  const highlightBeings = isMe
    ? ["rifts_esclings", "rifts_beings", "act3_transcended", "act4_ascended", "act5_soul_intro", "act5_supreme", "opp_attacks"].includes(phase)
    : ["act4_opp_transcends", "act4_after_attack", "opp_turn", "act6_position"].includes(phase);
  const highlightTomes = phase === "rifts_tomes";
  const highlightCurios = phase === "rifts_curios";
  const highlightProphecy = phase === "rifts_prophecy";
  const highlightExile = phase === "rifts_exile";
  const highlightStats = isMe && ["act2_anima", "act2_khao", "act2_curio_done", "act3_endturn"].includes(phase);

  // Pulse states
  const pulseSlot0Escling = isMe && phase === "act3_transcend_intro";  // tap Escling to Transcend
  const pulseSlot0Being   = isMe && (phase === "act4_combat_intro" || phase === "activate_uhlti");
  const pulseCurio0       = isMe && phase === "act2_curio_intro";

  const side = isMe ? "my" : "opp";

  return (
    <div style={{
      display: "flex", flexDirection: isMe ? "column" : "column-reverse", gap: 4,
      padding: "4px 10px",
      background: isMe
        ? `linear-gradient(180deg, transparent, rgba(109,40,217,0.08))`
        : `linear-gradient(180deg, rgba(153,27,27,0.07), transparent)`,
    }}>

      {/* Stat bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        background: highlightStats ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
        border: `1px solid ${highlightStats ? GM.inkGoldBright : isMe ? GM.inkGold + "55" : GM.rose + "44"}`,
        clipPath: facetTB(6), padding: "5px 11px",
        boxShadow: highlightStats
          ? `${edgeLight(GM.inkGoldBright, 0.4)}, ${bloom(GM.inkGold)}`
          : `${edgeLight(isMe ? GM.inkGold : GM.rose, 0.16)}, ${SHADOW_RAISED}`,
        transition: "all 0.3s",
      }}>
        <div style={{ fontSize: 11, color: isMe ? GM.inkGoldBright : GM.vellumDim, fontFamily: GM.font, fontStyle: "italic", fontWeight: 900, textShadow: isMe ? textGlow(GM.inkGold) : TEXT_SET }}>{player.name}</div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          {[
            { label: "♥", value: player.anima, color: GM.rose, tut: `${side}-anima` },
            { label: "✦", value: `${player.khao}/${player.maxKhao}`, color: GM.inkGoldBright, tut: `${side}-khao` },
            { label: "🃏", value: (player.hand || []).length, color: GM.silver, tut: null },
            { label: "💀", value: (player.necropolis || []).length, color: GM.vellumFaint, tut: null },
          ].map(s => (
            <div key={s.label} data-tut={s.tut || undefined} style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <span style={{ fontSize: 9, color: s.color }}>{s.label}</span>
              <span style={{ fontSize: 14, fontWeight: 900, color: s.color, fontFamily: GM.fontUI, textShadow: TEXT_SET }}>{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Beings row — real Shatterfield order: Prophecy | 4 battle rifts | Necropolis */}
      <div style={{ display: "flex", gap: 4, justifyContent: "center", alignItems: "flex-end", flexDirection: isMe ? "row" : "row-reverse" }}>
        {/* Prophecy */}
        <div data-tut={`${side}-prophecy`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          {prophecy
            ? <FC cardId={prophecy} size="tiny" faceDown flipped={!isMe} highlight={highlightProphecy} />
            : <EmptyRift size="tiny" flipped={!isMe} highlight={highlightProphecy} />
          }
          {isMe && <div style={{ fontSize: 6, color: GM.inkGold, letterSpacing: 1, opacity: 0.7, fontFamily: GM.font }}>PROP</div>}
        </div>

        {/* 4 Escling/Escendant slots (R110) — Esclings are placed pre-match,
            face-up, and always visible beneath a rising being (canon) */}
        {esclings.map((slot, i) => {
          const soul = slot?.soul || [];
          const isSupreme = soul.length >= 5;
          const slot0Escling = i === 0 && !slot?.escendant;
          const slot0Being = i === 0 && !!slot?.escendant;

          // Click handlers, only for the player's own field
          const click = isMe && i === 0
            ? (slot0Escling
                ? (pulseSlot0Escling ? onSlot0Escling : undefined)
                : (pulseSlot0Being ? onSlot0Being : undefined))
            : undefined;

          // Pulses
          const pulseThis    = isMe && i === 0 && slot0Being && pulseSlot0Being;
          const pulseEscling = isMe && slot0Escling && pulseSlot0Escling;

          return (
            <div key={i} data-tut={`${side}-slot-${i}`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              {slot?.escendant
                ? <FC cardId={slot.escendant} size="normal" currentLyf={slot.currentLyf} flipped={!isMe} soulCount={soul.length} pulse={pulseThis} highlight={highlightBeings} fallen={slot.fallen} onClick={click} />
                : <FC cardId={slot?.escling || "escling_dragon"} size="normal" flipped={!isMe} highlight={highlightBeings} pulse={pulseEscling} onClick={click} />
              }
              {isSupreme && isMe && i === 0 && (
                <div style={{ fontSize: 7, color: SD.gold, fontWeight: 900, letterSpacing: 1, textShadow: `0 0 6px ${SD.gold}` }}>A'AETERNIS</div>
              )}
              {isSupreme && !isMe && i === 0 && (
                <div style={{ fontSize: 7, color: SD.red, fontWeight: 900, letterSpacing: 1, textShadow: `0 0 6px ${SD.red}` }}>A'OETERNIS</div>
              )}
            </div>
          );
        })}

        {/* Necropolis */}
        <div data-tut={`${side}-necropolis`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          <div style={{ ...SZ.tiny, borderRadius: 7, background: GM.page, border: `1px solid ${GM.pageBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2, transform: !isMe ? "rotate(180deg)" : "none" }}>
            <span style={{ fontSize: 12, color: GM.vellumFaint }}>💀</span>
            <span style={{ fontSize: 11, color: GM.vellumFaint, fontWeight: 900 }}>{(player.necropolis || []).length}</span>
          </div>
        </div>

        {/* R112 — THE EXILE RIFT, to the RIGHT of the Necropolis, as the
            Shatterfield draws it. The pile and the void read differently
            on purpose: the Necropolis is where the ruined WAIT, and this
            is where they are forgotten. */}
        <div data-tut={`${side}-exile`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          <div style={{ ...SZ.tiny, borderRadius: 7, background: GM.page, border: `1px solid ${highlightExile ? "#7c3aed" : GM.pageBorder}`, boxShadow: highlightExile ? "0 0 12px #7c3aed88" : "none", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2, transform: !isMe ? "rotate(180deg)" : "none" }}>
            <span style={{ fontSize: 12, color: (player.exile || []).length ? "#8b6bd6" : GM.pageBorder }}>🕳</span>
            <span style={{ fontSize: 11, color: (player.exile || []).length ? "#8b6bd6" : GM.pageBorder, fontWeight: 900 }}>{(player.exile || []).length}</span>
          </div>
        </div>
      </div>

      {/* Tome + Curio row — real Shatterfield order: curio | 3 tomes | curio */}
      <div style={{ display: "flex", gap: 4, justifyContent: "center", alignItems: "center", flexDirection: isMe ? "row" : "row-reverse" }}>
        {/* Curio 0 */}
        <div data-tut={`${side}-curio-0`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          {curios[0]
            ? <FC cardId={curios[0]} size="tiny" flipped={!isMe} highlight={highlightCurios} pulse={pulseCurio0} />
            : <EmptyRift size="tiny" flipped={!isMe} highlight={highlightCurios} />
          }
        </div>

        {/* 3 Tome slots */}
        {tomes.map((tome, i) => {
          const tomeId = typeof tome === "object" ? tome?.id : tome;
          const faceDown = typeof tome === "object" ? (tome?.faceDown || false) : false;
          const tomeLyf = typeof tome === "object" ? tome?.currentLyf ?? null : null;
          return (
            <div key={i} data-tut={`${side}-tome-${i}`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              {tomeId
                ? <FC cardId={tomeId} size="small" faceDown={faceDown} flipped={!isMe} currentLyf={tomeLyf} highlight={highlightTomes} />
                : <EmptyRift size="small" flipped={!isMe} highlight={highlightTomes} />
              }
            </div>
          );
        })}

        {/* Curio 1 */}
        <div data-tut={`${side}-curio-1`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          {curios[1]
            ? <FC cardId={curios[1]} size="tiny" flipped={!isMe} highlight={highlightCurios} />
            : <EmptyRift size="tiny" flipped={!isMe} highlight={highlightCurios} />
          }
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// DRAW TILE — Lux/Grim framed tiles flanking the hand,
// matching the real Shatterfield's FramedDrawTile placement.
// ═══════════════════════════════════════════════════════════
function DrawTile({ icon, label, color, dataTut, highlight }) {
  return (
    <div data-tut={dataTut} style={{
      width: 42, height: 54, flexShrink: 0,
      background: highlight ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
      border: `1px solid ${highlight ? color : color + "44"}`,
      clipPath: facet(7),
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
      boxShadow: highlight
        ? `${edgeLight(color, 0.4)}, ${bloom(color)}`
        : `${edgeLight(color, 0.18)}, ${SHADOW_RAISED}`,
      transition: "all 0.3s",
    }}>
      <span style={{ fontSize: 15, filter: highlight ? `drop-shadow(0 0 5px ${color}88)` : "none" }}>{icon}</span>
      <span style={{ fontSize: 7, fontWeight: 900, letterSpacing: 1, color, fontFamily: GM.fontUI, textShadow: TEXT_SET }}>{label}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SOUL MODAL (simplified for tutorial)
// ═══════════════════════════════════════════════════════════
function SoulModal({ slot, onClose, onActivateShowStomper }) {
  const soul = slot?.soul || [];
  const soulCards = soul.map(id => getCard(id)).filter(Boolean);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,5,0.92)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 400, backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ background: OBSIDIAN_PANEL, border: `1px solid ${GM.inkGold}66`, clipPath: facet(12), padding: "24px 28px", minWidth: 320, maxWidth: 420, maxHeight: "80vh", overflowY: "auto", boxShadow: `${edgeLight(GM.inkGoldBright, 0.24)}, ${SHADOW_FLOAT}, inset 0 0 44px ${GM.amethyst}12` }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 4, marginBottom: 4, fontFamily: GM.fontUI, textShadow: textGlow(GM.inkGold) }}>✦ SOUL</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.fontD, fontStyle: "italic", marginBottom: 16, textShadow: textGlow(GM.inkGoldBright) }}>Premier Dragon Lord</div>
        <div style={{ fontSize: 9, color: GM.amethystBright, letterSpacing: 2, marginBottom: 10, fontFamily: GM.fontUI }}>A'AETERNIS — SUPREME BEING</div>
        {soulCards.map((sc, i) => {
          const isUhlti = sc.type === "U'hlti";
          const isAff = sc.type === "Affinity";
          const color = isUhlti ? GM.inkGoldBright : isAff ? GM.amethystBright : GM.inkGold;
          const isShowStomper = sc.id === "u_premier_dragon_lord";
          // Was a vyren-pattern bridge: the internal type id read "Primera"
          // and players had to be shown IMPERA. cards.js types these
          // "Impera" now, so upper-casing the type IS the label.
          const typeLabel = sc.type?.toUpperCase();
          return (
            <div key={i} style={{ background: OBSIDIAN_SLAB, border: `1px solid ${color}55`, clipPath: facetTB(7), padding: "12px 14px", marginBottom: 8, boxShadow: `${edgeLight(color, 0.14)}, ${SHADOW_RAISED}` }}>
              <div style={{ fontSize: 9, color, fontWeight: 900, letterSpacing: 1, marginBottom: 3, fontFamily: GM.fontUI }}>{typeLabel}</div>
              <div style={{ fontSize: 14, fontWeight: 900, color: GM.vellum, fontFamily: GM.font, fontStyle: "italic", marginBottom: 6, textShadow: TEXT_SET }}>{sc.name}</div>
              {sc.effect && <div style={{ fontSize: 11, color: GM.vellumDim, lineHeight: 1.6, marginBottom: isShowStomper ? 10 : 0, fontFamily: GM.fontBody }}>{sc.effectTag && <span style={{ color: GM.inkGoldBright, fontWeight: 900 }}>{sc.effectTag}: </span>}{sc.effect}</div>}
              {isShowStomper && (
                <button onClick={onActivateShowStomper} style={{ width: "100%", padding: "10px", background: OBSIDIAN_SLAB_LIT, border: `1px solid ${GM.inkGold}`, clipPath: facetTB(6), cursor: "pointer", color: GM.inkGoldBright, fontFamily: GM.font, fontSize: 12, fontWeight: 900, fontStyle: "italic", boxShadow: `${edgeLight(GM.inkGoldBright, 0.3)}, ${bloom(GM.inkGold)}`, animation: "tutPulse 1s ease-in-out infinite", textShadow: textGlow(GM.inkGoldBright) }}>
                  ✦ Activate — ♥10 Anima + ✦10 Khao
                </button>
              )}
            </div>
          );
        })}
        <button onClick={onClose} style={{ width: "100%", padding: "10px", background: OBSIDIAN_SLAB, border: `1px solid ${GM.silverDim}`, clipPath: facetTB(6), cursor: "pointer", color: GM.vellumDim, fontFamily: GM.font, fontSize: 11, marginTop: 4, boxShadow: `${edgeLight(GM.silver, 0.12)}, ${SHADOW_RAISED}`, textShadow: TEXT_SET }}>Close</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// NARRATION BUBBLE — floating, non-blocking
// ═══════════════════════════════════════════════════════════
function NarrationPanel({ phase, onBtn, onRulebook }) {
  const p = PHASES.find(x => x.id === phase);
  const [lineIdx, setLineIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  const lines = p ? p.body.split("\n").filter(l => l.trim()) : [];

  useEffect(() => { setLineIdx(0); setVisible(true); }, [phase]);

  if (!p) return null;
  const isVictory = phase === "victory";
  const isLast = lineIdx >= lines.length - 1;
  const currentLine = lines[lineIdx] || "";

  const advance = () => {
    if (!isLast) {
      setVisible(false);
      setTimeout(() => { setLineIdx(i => i + 1); setVisible(true); }, 160);
    } else if (p.btn) {
      onBtn();
    }
  };

  const hint = isLast && p.btn ? p.btn + " ›"
    : isLast && phase === "activate_uhlti" ? "↑ tap your A'aeternis"
    : isLast && phase === "act3_transcend_intro" ? "↑ tap your Escling"
    : isLast && phase === "act4_combat_intro" ? "↑ tap your being"
    : isLast && (phase === "opp_turn" || phase === "act2_curio_intro" || phase === "act3_endturn" || phase === "act4_opp_transcends" || phase === "act4_ascend" || phase === "act5_train1_done" || phase === "act5_train2" || phase === "act5_lord" || phase === "act5_lvl3_uhlti" || phase === "act5_affinity" || phase === "act6_timeskip") ? "watching..."
    : "tap ›";

  return (
    /* Floats in lower-center of screen, does NOT stretch full width */
    <div style={{ position: "fixed", bottom: 104, left: 12, zIndex: 50,
      display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8,
      pointerEvents: "none",
      maxWidth: "min(55vw, 280px)",
    }}>

      {/* J'ynxi avatar + speech bubble row */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, pointerEvents: "all" }}>

        {/* J'ynxi portrait — gold-metal ring */}
        <div style={{ flexShrink: 0, width: 50, height: 50, borderRadius: "50%", overflow: "hidden",
          border: "2px solid transparent",
          background: `${OBSIDIAN_PANEL} padding-box, ${GOLD_METAL} border-box`,
          boxShadow: `0 0 18px ${GM.inkGold}3a, ${SHADOW_RAISED}`,
        }}>
          <img src={`${CDN}/jynxi.webp`} alt="J'ynxi"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = "<span style='font-size:24px;display:flex;align-items:center;justify-content:center;height:100%'>🃏</span>"; }}
          />
        </div>

        {/* Speech bubble — obsidian panel, gold when victorious */}
        <div onClick={advance} style={{
          position: "relative",
          background: isVictory ? OBSIDIAN_SLAB_LIT : OBSIDIAN_PANEL,
          border: `1px solid ${isVictory ? GM.inkGold + "aa" : GM.pageBorder}`,
          borderRadius: "16px 16px 16px 4px",
          padding: "10px 15px 8px",
          cursor: "pointer",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.16s ease",
          boxShadow: isVictory
            ? `${edgeLight(GM.inkGoldBright, 0.3)}, ${SHADOW_FLOAT}, ${bloom(GM.inkGold)}`
            : `${edgeLight(GM.silver, 0.12)}, ${SHADOW_FLOAT}`,
          userSelect: "none",
          maxWidth: "min(50vw, 250px)",
          width: "max-content",
          fontFamily: GM.font,
        }}>
          {/* Speaker name */}
          <div style={{ fontSize: 8, color: isVictory ? GM.inkGoldBright : GM.inkGold, fontWeight: 900, letterSpacing: 2, marginBottom: 5, fontFamily: GM.fontUI, textShadow: textGlow(GM.inkGold) }}>
            J'YNXI {isVictory ? "◆" : "·"} {p.title.toUpperCase()}
          </div>

          {/* Message text — wraps naturally */}
          <div style={{ fontSize: 13, color: GM.vellum, lineHeight: 1.7, fontFamily: GM.fontBody,
            maxWidth: "min(48vw, 230px)", whiteSpace: "normal", textShadow: TEXT_SET,
          }}>
            {currentLine}
          </div>

          {/* Footer row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 12 }}>
            {/* Line dots */}
            <div style={{ display: "flex", gap: 3 }}>
              {lines.map((_, i) => (
                <div key={i} style={{ width: i === lineIdx ? 12 : 4, height: 3, borderRadius: 2,
                  background: i < lineIdx ? GM.inkGold + "99" : i === lineIdx ? GM.inkGold : GM.pageBorder,
                  transition: "all 0.2s" }} />
              ))}
            </div>
            {/* Action hint */}
            <div style={{ fontSize: 9, color: isVictory ? GM.inkGoldBright : (isLast && p.btn ? GM.inkGold : GM.vellumFaint),
              fontStyle: "italic", fontWeight: isLast && p.btn ? 900 : 400,
              animation: isLast && (phase === "activate_uhlti" || phase === "act3_transcend_intro" || phase === "act4_combat_intro") ? "tutPulse 1s ease-in-out infinite" : "none",
              whiteSpace: "nowrap",
            }}>
              {hint}
            </div>
          </div>

          {/* Victory — study path */}
          {isVictory && isLast && onRulebook && (
            <button
              onClick={e => { e.stopPropagation(); onRulebook(); }}
              style={{ width: "100%", marginTop: 10, padding: "9px 10px", background: OBSIDIAN_SLAB, border: `1px solid ${GM.silverDim}`, clipPath: facetTB(6), cursor: "pointer", color: GM.silverBright, fontFamily: GM.font, fontSize: 10, fontStyle: "italic", letterSpacing: 1, boxShadow: `${edgeLight(GM.silver, 0.16)}, ${SHADOW_RAISED}`, textShadow: TEXT_SET }}
            >
              📖 Study the Grimoire of Terror →
            </button>
          )}
        </div>
      </div>

      {/* Act-progress dots */}
      <div style={{ display: "flex", gap: 6, paddingLeft: 60 }}>
        {[1, 2, 3, 4, 5, 6].map(actNum => {
          const curAct = p.act || 1;
          return <div key={actNum} style={{
            width: actNum === curAct ? 16 : 6, height: 4, borderRadius: 2,
            background: actNum < curAct ? GM.inkGold : actNum === curAct ? GM.inkGoldBright : GM.pageBorder,
            transition: "all 0.3s",
            boxShadow: actNum === curAct ? `0 0 6px ${GM.inkGold}88` : "none",
          }} />;
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ACT TITLE CARD — cinematic interstitial on act change
// ═══════════════════════════════════════════════════════════
const ACT_TITLES = {
  1: "The Shatterfield",
  2: "Setup & The Opening",
  3: "Transcending",
  4: "Combat & Ascending",
  5: "Soul & Supreme",
  6: "The Finale",
};
const ROMAN = { 1: "I", 2: "II", 3: "III", 4: "IV", 5: "V", 6: "VI" };

function ActTitleCard({ act }) {
  if (!act) return null;
  return (
    <div key={act} style={{
      position: "fixed", inset: 0, zIndex: 300, pointerEvents: "none",
      display: "flex", alignItems: "center", justifyContent: "center",
      animation: "tutActCard 1.6s ease forwards",
    }}>
      {/* gold-metal frame: outer strip + inner obsidian slab */}
      <div style={{
        background: GOLD_METAL, padding: 2, clipPath: facet(11),
        filter: `drop-shadow(0 0 44px rgba(0,0,0,0.85)) drop-shadow(0 0 22px ${GM.inkGold}3a)`,
      }}>
        <div style={{
          position: "relative", overflow: "hidden",
          background: OBSIDIAN_PANEL, clipPath: facet(10),
          padding: "18px 40px", textAlign: "center",
          boxShadow: `${edgeLight(GM.inkGoldBright, 0.28)}, inset 0 0 40px ${GM.amethyst}14`,
        }}>
          {/* shimmer sweep across the plate */}
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none",
            background: "linear-gradient(105deg, transparent 40%, rgba(245,216,120,0.16) 50%, transparent 60%)",
            backgroundSize: "220% 100%", animation: "tutSweep 1.6s ease forwards",
          }} />
          <div style={{ position: "relative", fontSize: 9, color: GM.inkGold, letterSpacing: 6, fontFamily: GM.fontUI, marginBottom: 7, textShadow: textGlow(GM.inkGold) }}>◆ ACT {ROMAN[act]} ◆</div>
          <div style={{ position: "relative", fontSize: 21, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.fontD, fontStyle: "italic", letterSpacing: 1, textShadow: textGlow(GM.inkGoldBright) }}>{ACT_TITLES[act]}</div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TUTORIAL ENGINE — the six-act scripted Solrend
// ═══════════════════════════════════════════════════════════
function TutorialEngine({ playerName, onExit, onRulebook }) {
  const [phase, setPhase] = useState("intro");
  const [player, setPlayer] = useState(() => ACT1_PLAYER(playerName));
  const [opp, setOpp] = useState(ACT1_OPP);
  const [soulOpen, setSoulOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [actCard, setActCard] = useState(null);   // act # currently flashing
  const [seenActs, setSeenActs] = useState(() => new Set());

  // Track which phase mutations have already run so re-renders during a
  // phase don't re-fire the mutate function (which would clobber any
  // intermediate handler-driven state changes).
  const [mutatedPhases, setMutatedPhases] = useState(new Set());

  const showToast = useCallback((msg, color = GM.inkGold, duration = 2500) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), duration);
  }, []);

  const curPhase = PHASES.find(p => p.id === phase);

  // ── Act title cards — flash once per act, including Act I ──
  useEffect(() => {
    const act = curPhase?.act;
    if (!act || seenActs.has(act)) return;
    setSeenActs(prev => { const n = new Set(prev); n.add(act); return n; });
    setActCard(act);
    const t = setTimeout(() => setActCard(null), 1650);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curPhase?.act]);

  // ── Run phase mutations on entry ──────────────────────────
  useEffect(() => {
    if (!curPhase || !curPhase.mutate) return;
    if (mutatedPhases.has(phase)) return;
    const result = curPhase.mutate(player, opp, playerName);
    if (result?.player) setPlayer(result.player);
    if (result?.opp) setOpp(result.opp);
    setMutatedPhases(prev => { const n = new Set(prev); n.add(phase); return n; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Auto-advance for narrative-only phases ────────────────
  useEffect(() => {
    if (!curPhase || !curPhase.autoAdvanceMs) return;

    // Special-case opp_turn — the Shade flips Nothing Left (3 Khao + 1 own
    // Anima → 9), ruining the player's shield-Tome; then Chaos Dragon
    // Lord fractures the player's A'aeternis. Canon: no Anima is lost to
    // the ruin itself — only Nothing Left's self-dim moves a life total.
    if (phase === "opp_turn") {
      const t = setTimeout(() => {
        setPlayer(p => {
          const f = JSON.parse(JSON.stringify(p.field));
          if (f.tomes && f.tomes[0]) f.tomes[0] = null;
          if (f.esclings && f.esclings[0]) f.esclings[0] = { ...f.esclings[0], currentLyf: 0 };
          return { ...p, field: f, necropolis: [...(p.necropolis || []), "tome_khao_duhmdumn_champion"] };
        });
        setOpp(o => {
          const f = JSON.parse(JSON.stringify(o.field));
          if (f.tomes && f.tomes[0]) f.tomes[0] = null;
          return { ...o, field: f, anima: 9, khao: Math.max(0, o.khao - 3), necropolis: [...(o.necropolis || []), "tome_nothing_left"] };
        });
        showToast("Chaos Dragon Lord — Lyt 10 fractures your A'aeternis!", SD.red);
        setTimeout(() => setPhase("opp_attacks"), 900);
      }, curPhase.autoAdvanceMs);
      return () => clearTimeout(t);
    }

    // Generic auto-advance — step to the next phase in PHASES order
    const t = setTimeout(() => {
      const idx = PHASES.findIndex(x => x.id === phase);
      if (idx >= 0 && idx < PHASES.length - 1) setPhase(PHASES[idx + 1].id);
    }, curPhase.autoAdvanceMs);
    return () => clearTimeout(t);
  }, [phase, curPhase, showToast]);

  // ── Button handler — the bubble's "→" / "Continue" ────────
  const handleBtn = () => {
    if (phase === "victory") { onExit(); return; }
    const idx = PHASES.findIndex(p => p.id === phase);
    if (idx < PHASES.length - 1) setPhase(PHASES[idx + 1].id);
  };

  // ── Tap handlers — gated by waitFor ───────────────────────

  // Act II's Curio grant is the PHASE MUTATE now, not a tap handler.
  // The board wakes every Curio from its duel-open listener and has no
  // tap path at all, so a handler here would be teaching a gesture that
  // does not exist — and a live handler with no caller is its own trap.
  // Act III: tap your Escling → Natural Transcend (costs nothing)
  const handleSlot0Escling = () => {
    if (phase !== "act3_transcend_intro") return;
    setPlayer(p => {
      const f = JSON.parse(JSON.stringify(p.field));
      f.esclings[0] = {
        escling: "escling_dragon",
        escendant: "premier_dragon_beb",
        currentLyf: 4, soul: [], natural: true,
      };
      // Consume one Beb from hand — Natural Transcending costs no Khao.
      const hand = [...p.hand];
      const i = hand.indexOf("premier_dragon_beb");
      if (i >= 0) hand.splice(i, 1);
      return { ...p, field: f, hand };
    });
    showToast("🐉 Premier Dragon Beb — Transcended · 0 Khao", GM.inkGold);
    setTimeout(() => setPhase("act3_transcended"), 800);
  };

  // Act IV: tap your being → attack opp's Beb
  // Act VI: tap your Lord → open Soul modal
  const handleSlot0Being = () => {
    if (phase === "act4_combat_intro") {
      // Resolve combat: Chaos Beb is printed Lyf 0 — already Fractured.
      // One Lyt Attack ruins it. Canon: no Anima is lost to a ruin —
      // being to Necropolis, Escling remains.
      setOpp(o => {
        const f = JSON.parse(JSON.stringify(o.field));
        f.esclings[0] = { escling: "escling_dragon", escendant: null, currentLyf: null, soul: [] };
        return {
          ...o,
          field: f,
          necropolis: [...(o.necropolis || []), "chaos_dragon_beb"],
        };
      });
      showToast("⚔ Chaos Dragon Beb ruined — to the Necropolis", SD.red);
      setTimeout(() => setPhase("act4_after_attack"), 1000);
      return;
    }
    if (phase === "activate_uhlti") {
      setPhase("soul_open");
      setSoulOpen(true);
      return;
    }
  };

  // Act VI: activate Show Stomper from Soul modal
  const handleActivateShowStomper = () => {
    setSoulOpen(false);
    setPlayer(p => ({ ...p, khao: p.khao - 10, anima: p.anima - 10 }));
    setOpp(o => ({ ...o, anima: 0 }));
    showToast("✦ Show Stomper — Opponent Anima dimmed by 10!", SD.gold);
    setTimeout(() => setPhase("victory"), 1000);
  };

  // Tap-on-being only fires for the player and only on phases that need it.
  const slot0BeingHandler = (phase === "act4_combat_intro" || phase === "activate_uhlti")
    ? handleSlot0Being : undefined;

  return (
    <div style={{ position: "fixed", inset: 0, background: GM.page, display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: GM.font }}>
      <UnstableWorldBG />
      <GlassCracks opacity={0.08} />

      {/* Header */}
      <div style={{ position: "relative", zIndex: 10, flexShrink: 0, background: `${GM.page}f2`, borderBottom: `1px solid ${GM.pageBorder}`, boxShadow: `0 1px 0 ${GM.inkGold}1e, ${SHADOW_RAISED}`, backdropFilter: "blur(8px)", padding: "8px 14px", display: "flex", alignItems: "center" }}>
        <button onClick={onExit} style={{ background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`, color: GM.vellumDim, padding: "5px 12px", clipPath: facetTB(5), cursor: "pointer", fontFamily: GM.fontUI, fontSize: 9, letterSpacing: 1, boxShadow: `${edgeLight(GM.silver, 0.12)}, ${SHADOW_RAISED}`, textShadow: TEXT_SET }}>✕ EXIT</button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: 8, color: GM.inkGold, letterSpacing: 6, fontFamily: GM.fontUI, textShadow: textGlow(GM.inkGold) }}>
            ◆ TUTORIAL I · ACT {curPhase?.act || 1} ◆
          </div>
        </div>
        <div style={{ width: 56 }} />
      </div>

      {/* Shatterfield */}
      <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden" }}>
        {/* Opponent side (flipped) */}
        <PlayerSide player={opp} isMe={false} phase={phase} />

        {/* Center divider */}
        <div style={{ flexShrink: 0 }}>
          <OrnateDivider />
        </div>

        {/* Player side */}
        <PlayerSide
          player={player}
          isMe
          phase={phase}
          onSlot0Escling={handleSlot0Escling}
          onSlot0Being={slot0BeingHandler}
        />
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 56, left: "50%", transform: "translateX(-50%)", background: OBSIDIAN_SLAB_LIT, border: `1px solid ${toast.color}`, clipPath: facetTB(6), padding: "8px 18px", fontSize: 12, color: toast.color, zIndex: 600, pointerEvents: "none", fontWeight: 900, whiteSpace: "nowrap", fontFamily: GM.font, boxShadow: `${edgeLight(toast.color, 0.3)}, ${SHADOW_FLOAT}`, filter: `drop-shadow(0 0 10px ${toast.color}44)`, textShadow: TEXT_SET }}>
          {toast.msg}
        </div>
      )}

      {/* Soul modal */}
      {soulOpen && (
        <SoulModal
          slot={player.field.esclings[0]}
          onClose={() => { setSoulOpen(false); setPhase("activate_uhlti"); }}
          onActivateShowStomper={handleActivateShowStomper}
        />
      )}

      {/* Hand strip — LUX | hand | GRIM, matching the real Shatterfield */}
      <div style={{ position: "relative", zIndex: 10, flexShrink: 0, background: `${GM.page}f2`, borderTop: `1px solid ${GM.pageBorder}`, boxShadow: `0 -1px 0 ${GM.inkGold}1e, inset 0 1px 0 rgba(226,232,244,0.05)`, padding: "8px 12px", display: "flex", gap: 10, alignItems: "flex-end", justifyContent: "center", minHeight: 74 }}>
        <DrawTile icon="✦" label="LUX" color={GM.inkGoldBright} dataTut="my-lux" highlight={phase === "rifts_grimoire" || phase === "act5_train1"} />

        <div data-tut="my-hand" style={{ display: "flex", gap: 5, overflowX: "auto", alignItems: "flex-end", minWidth: 60, minHeight: 58, padding: "2px 4px" }}>
          {player.hand.length === 0 ? (
            <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", alignSelf: "center", padding: "0 10px", fontFamily: GM.fontBody }}>
              {phase === "victory" ? "victorious" : "— empty —"}
            </div>
          ) : (
            player.hand.map((cardId, i) => (
              <div key={`${cardId}-${i}`} style={{ flexShrink: 0, animation: "tutFadeUp 0.4s ease both", animationDelay: `${i * 0.07}s` }}>
                <FC cardId={cardId} size="hand" />
              </div>
            ))
          )}
        </div>

        <DrawTile icon="📖" label="GRIM" color={GM.amethystBright} dataTut="my-grim" highlight={phase === "rifts_grimoire" || phase === "act5_lord"} />
      </div>

      {/* Guide layer — arrows, rings & pointers anchored to the field */}
      <GuideLayer phase={phase} player={player} opp={opp} />

      {/* Act interstitial — cinematic title card */}
      <ActTitleCard act={actCard} />

      {/* J'ynxi narration bubble — fixed overlay, does not block field */}
      <NarrationPanel phase={phase} onBtn={handleBtn} onRulebook={onRulebook} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TITLE SCREEN — the cold open (replaces the old hub)
// ═══════════════════════════════════════════════════════════
function TitleScreen({ onBack, onBegin, onRulebook }) {
  // C5 backdrop wave (2026-08-18): the tutorial's title hub lives inside
  // the ONE canonical room rectangle; root is transparent so the equipped
  // backdrop letterboxes around it. The lesson itself (TutorialEngine)
  // stays fullscreen — it is gameplay, not a room.
  const stage = useStageRect();
  return (
    <div style={{ position: "fixed", inset: 0, background: "transparent", fontFamily: GM.font, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
    <div style={{ position: "relative", width: stage.w, height: stage.h, flexShrink: 0, background: MANUSCRIPT_BG, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <UnstableWorldBG />
      <GlassCracks opacity={0.06} />
      <StarDust count={26} />

      {/* Back — quiet, top-left */}
      <button onClick={onBack} style={{ position: "absolute", top: 16, left: 16, zIndex: 5, background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`, color: GM.vellumDim, padding: "6px 14px", clipPath: facetTB(5), cursor: "pointer", fontFamily: GM.fontUI, fontSize: 9, letterSpacing: 1, boxShadow: `${edgeLight(GM.silver, 0.12)}, ${SHADOW_RAISED}`, textShadow: TEXT_SET }}>← BACK</button>

      <div style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: 440, padding: "0 24px", textAlign: "center", animation: "tutFadeUp 0.7s ease both" }}>

        {/* J'ynxi — your guide */}
        <div style={{ width: 84, height: 84, borderRadius: "50%", overflow: "hidden", margin: "0 auto 18px", border: "2px solid transparent", background: `${OBSIDIAN_PANEL} padding-box, ${GOLD_METAL} border-box`, boxShadow: `0 0 32px ${GM.inkGold}3a, ${SHADOW_FLOAT}` }}>
          <img src={`${CDN}/jynxi.webp`} alt="J'ynxi"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = "<span style='font-size:38px;display:flex;align-items:center;justify-content:center;height:100%'>🃏</span>"; }}
          />
        </div>

        {/* Title */}
        <div style={{ position: "relative", marginBottom: 8 }}>
          <CornerFlourish size={24} color={GM.inkGold} style={{ position: "absolute", top: -6, left: 0 }} />
          <CornerFlourish size={24} color={GM.inkGold} style={{ position: "absolute", top: -6, right: 0, transform: "scaleX(-1)" }} />
          <div style={{ fontSize: 9, color: GM.inkGold, letterSpacing: 8, marginBottom: 6, fontFamily: GM.fontUI, textShadow: textGlow(GM.inkGold) }}>◆ LEARN THE SOLREND ◆</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.fontD, letterSpacing: 4, animation: "tutTitleGlow 3.5s ease-in-out infinite" }}>How to Play</div>
        </div>

        <OrnateDivider style={{ margin: "10px 0 16px" }} />

        <div style={{ fontSize: 12, color: GM.vellumDim, lineHeight: 1.9, fontStyle: "italic", marginBottom: 26 }}>
          Six acts. One Solrend. J'ynxi deals — you play.<br />
          By the end, you'll have crowned a Supreme Being.
        </div>

        {/* Primary — Begin — gold-metal frame over lit obsidian */}
        <div style={{ background: GOLD_METAL, padding: 2, clipPath: facet(9), marginBottom: 12, filter: `drop-shadow(0 6px 16px rgba(0,0,0,0.6)) drop-shadow(0 0 20px ${GM.inkGold}2a)` }}>
          <button onClick={onBegin} style={{
            width: "100%", padding: "16px 20px", cursor: "pointer",
            position: "relative", overflow: "hidden",
            background: OBSIDIAN_SLAB_LIT, border: "none", clipPath: facet(8),
            color: GM.inkGoldBright, fontFamily: GM.font,
            boxShadow: `${edgeLight(GM.inkGoldBright, 0.3)}, inset 0 0 30px ${GM.amethyst}14`,
          }}>
            <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "linear-gradient(105deg, transparent 42%, rgba(245,216,120,0.14) 50%, transparent 58%)", backgroundSize: "220% 100%", animation: "tutSweep 3.2s ease-in-out infinite" }} />
            <div style={{ position: "relative", fontSize: 15, fontWeight: 900, fontStyle: "italic", letterSpacing: 1, marginBottom: 4, textShadow: textGlow(GM.inkGoldBright) }}>⚔ Begin — Your First Solrend</div>
            <div style={{ position: "relative", fontSize: 8, color: GM.inkGold, letterSpacing: 3, fontFamily: GM.fontUI }}>8 MINUTES · GUIDED · NO READING REQUIRED</div>
          </button>
        </div>

        {/* Secondary — Grimoire */}
        <button onClick={onRulebook} style={{
          width: "100%", padding: "12px 20px", cursor: "pointer",
          background: OBSIDIAN_SLAB,
          border: `1px solid ${GM.silverDim}`, clipPath: facetTB(7),
          color: GM.silverBright, fontFamily: GM.font,
          boxShadow: `${edgeLight(GM.silver, 0.14)}, ${SHADOW_RAISED}`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, fontStyle: "italic", letterSpacing: 1, marginBottom: 3, textShadow: TEXT_SET }}>📖 The Grimoire of Terror</div>
          <div style={{ fontSize: 8, color: GM.vellumDim, letterSpacing: 2, fontFamily: GM.fontUI }}>EVERY RULE · EVERY MECHANIC · SEARCHABLE</div>
        </button>
      </div>
    </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// DEFAULT EXPORT — HOW TO PLAY (title screen → tutorial)
// ═══════════════════════════════════════════════════════════
export default function HowToPlay({ onBack, onRulebook, playerName }) {
  const [mode, setMode] = useState("title"); // "title" | "tutorial"

  if (mode === "tutorial") return (
    <TutorialEngine
      playerName={playerName || "Aenarchist"}
      onExit={onBack}
      onRulebook={onRulebook}
    />
  );

  return (
    <TitleScreen
      onBack={onBack}
      onBegin={() => setMode("tutorial")}
      onRulebook={onRulebook}
    />
  );
}
